/**
 * The automation's source table choice ("None" runs the workflow once per
 * trigger without rows). Changing the table resets everything that belongs
 * to the old one. The sidebar's input step and the settings panel's source
 * setting render this one field.
 */
import { useI18n } from '@affine/i18n';
import { type FC, useMemo } from 'react';

import {
  type AutomationConfig,
  DEFAULT_STATUS_TRACKING_CONFIG,
} from '../../services/automation';
import type { DatabaseInfo } from '../../services/database-discovery';
import { DEFAULT_TRIGGER_CONFIG, isRowTrigger } from '../../types';
import { isMailFolderTable } from './source-table-rules';
import { TableSelector } from './table-selector';

export interface SourceTableFieldProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
}

export const SourceTableField: FC<SourceTableFieldProps> = ({
  config,
  onUpdate,
}) => {
  const t = useI18n();
  const triggerConfig = config.triggerConfig ?? DEFAULT_TRIGGER_CONFIG;
  const statusTracking =
    config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG;
  const mailFolderTables = useMemo(
    () => ({
      matches: isMailFolderTable,
      note: t['com.streamlain.automation.sidebar.source-mail-folder-hint'](),
    }),
    [t]
  );

  const handleSourceTableChange = (database: DatabaseInfo) => {
    onUpdate({
      sourceTableId: database.blockId,
      sourceTableName: database.databaseTitle,
      sourceDocId: database.docId, // E018-S008-T005: Store docId for CrossDoc indicator
      // Reset all table-dependent config when source table changes
      selectedColumns: [],
      inputMappings: [],
      filterGroup: undefined, // Reset filters
      inputTables: [], // sl-m379: Reset secondary tables (auto-detected will regenerate)
      // sl-za0v.13.1: Reset batch size on table change (was missing — orphaned settings bug)
      ...(config.batchConfig
        ? {
            batchConfig: {
              ...config.batchConfig,
              batchSize: 1,
              executionMode: 'row',
            },
          }
        : {}),
      // Disable status tracking (column IDs are source-table-specific)
      statusTracking: {
        ...statusTracking,
        enabled: false,
        columnId: null,
      },
      // Watched columns are column IDs of the old table too.
      ...(triggerConfig.watchedColumnIds?.length
        ? { triggerConfig: { ...triggerConfig, watchedColumnIds: [] } }
        : {}),
      // NOTE: outputMappings are NOT reset here — they map workflow response
      // fields to TARGET table columns, not source table columns.
    });
  };

  /** "None": the workflow runs once per trigger without input rows. */
  const handleClearSource = () => {
    onUpdate({
      sourceTableId: null,
      sourceTableName: null,
      sourceDocId: null,
      selectedColumns: [],
      filterGroup: undefined,
      inputMappings: [],
      inputTables: [],
      statusTracking: {
        ...statusTracking,
        enabled: false,
        columnId: null,
      },
    });
  };

  return (
    <TableSelector
      value={config.sourceTableId || undefined}
      onChange={handleSourceTableChange}
      placeholder={t['com.streamlain.streamlain.input.select-source-table']()}
      unavailable={mailFolderTables}
      noneOption={
        isRowTrigger(triggerConfig.type)
          ? undefined
          : {
              label: t['com.streamlain.automation.sidebar.source-none'](),
              hint: t['com.streamlain.automation.sidebar.source-none-hint'](),
              selected: !config.sourceTableId,
              onSelect: handleClearSource,
            }
      }
    />
  );
};
